// docs.unacast.com → Pine59 docs mapping (CAT-1204).
//
// The old Unacast documentation (Docusaurus on GitHub Pages) forwards every
// visitor to the *same path* under `/docs` on this host:
//
//   docs.unacast.com/datasets/metrics/foot_traffic
//     → console.pine59.com/docs/datasets/metrics/foot_traffic
//
// Keeping the path makes the redirect on the Unacast side a dumb host swap,
// the same contract as the unacast.com blog posts (pine59.com/post/<slug>).
// The mapping to the real Pine59 page lives here instead: the route layer
// looks the old path up, 301s to the target with `?from-docs=<old path>`,
// and the docs root shows a dialog explaining the move (LegacyDocsDialog).
//
// Two kinds of match: `equivalent` when the Pine59 page covers the same
// topic, `nearest` when the old page has no counterpart (DaaS file delivery,
// per-dataset schemas for US products not yet in the public catalog, tag
// listings, …) and we land the visitor on the closest starting point.

export type LegacyDocsMatch = "equivalent" | "nearest"

export interface LegacyDocsTarget {
  /**
   * Pine59 docs path (root-relative, may carry a hash), or an absolute URL
   * on another Pine59 host. Absolute targets get no `?from-docs` marker:
   * the landing site does not render the dialog.
   */
  to: string
  match: LegacyDocsMatch
}

/** Query param carrying the old docs.unacast.com path to the destination. */
export const LEGACY_DOCS_PARAM = "from-docs"

const GUIDES = "/docs/guides"
const TERMS = "https://pine59.com/terms"
const CUSTOM_LOCATIONS_API = "/docs/api#tag/custom-locations"

const eq = (to: string): LegacyDocsTarget => ({ to, match: "equivalent" })
const near = (to: string): LegacyDocsTarget => ({ to, match: "nearest" })

/** Exact old path (no trailing slash) → target. */
const EXACT: Record<string, LegacyDocsTarget> = {
  // Docusaurus category landing pages.
  "/category/delivery": near("https://pine59.com/solutions"),
  "/category/location-types": eq("/docs/concepts/location-types"),
  "/category/metrics": eq("/docs/concepts/metrics"),
  "/category/schemas": near("/docs/datasets/usa"),

  // Delivery: API, DaaS (discontinued), SaaS.
  "/datasets/delivery/api/standard-locations": eq("/docs/guides/first-query"),
  "/datasets/delivery/api/custom-locations": eq(CUSTOM_LOCATIONS_API),
  "/datasets/delivery/insights": eq("/docs/guides/no-code"),

  // FAQ was about the US ML model; there is no FAQ in the new docs.
  "/datasets/faq": near(GUIDES),

  // Location types.
  "/datasets/locations/points_of_interest": eq(
    "/docs/datasets/usa/location-sets/safegraph_places_usa",
  ),
  "/datasets/locations/census_block_groups": near(
    "/docs/concepts/location-types/area",
  ),
  "/datasets/locations/custom_boundaries": near(CUSTOM_LOCATIONS_API),

  // Metric descriptions → concept pages. The bare ids (`foot-traffic`) are
  // aliases that redirect to the per-location-type page; target those
  // directly so the visitor gets a single hop.
  "/datasets/metrics/foot_traffic": eq(
    "/docs/concepts/metrics/foot-traffic-place",
  ),
  "/datasets/metrics/visit_length": eq(
    "/docs/concepts/metrics/visit-length-place",
  ),
  "/datasets/metrics/popular_times": eq(
    "/docs/concepts/metrics/popular-times-place",
  ),
  "/datasets/metrics/trade_areas": eq(
    "/docs/concepts/metrics/trade-area-place",
  ),
  "/datasets/metrics/demographics": eq(
    "/docs/concepts/metrics/visitor-demographics-place",
  ),
  "/datasets/metrics/visitor_journey": eq(
    "/docs/concepts/metrics/visitor-journey-place",
  ),

  // Schemas: the US monthly/weekly datasets are not in the public catalog
  // yet, so the field-level schema pages have no counterpart. Land on the
  // metric concept page for the same metric.
  "/datasets/schemas/places/places-schema": eq(
    "/docs/datasets/usa/location-sets/safegraph_places_usa",
  ),
  "/datasets/schemas/places/foot_traffic_new": near(
    "/docs/concepts/metrics/foot-traffic-place",
  ),
  "/datasets/schemas/places/foot-traffic": near(
    "/docs/concepts/metrics/foot-traffic-place",
  ),
  "/datasets/schemas/places/visit_length": near(
    "/docs/concepts/metrics/visit-length-place",
  ),
  "/datasets/schemas/places/popular_times": near(
    "/docs/concepts/metrics/popular-times-place",
  ),
  "/datasets/schemas/places/trade_areas_cbg": near(
    "/docs/concepts/metrics/trade-area-place",
  ),
  "/datasets/schemas/places/demographics": near(
    "/docs/concepts/metrics/visitor-demographics-place",
  ),
  "/datasets/schemas/places/visitor_journey": near(
    "/docs/concepts/metrics/visitor-journey-place",
  ),
  "/datasets/schemas/neighborhoods/census-block-groups": near(
    "/docs/concepts/location-types/area",
  ),
  "/datasets/schemas/neighborhoods/foot-traffic": near(
    "/docs/concepts/metrics/foot-traffic-area",
  ),
  "/datasets/schemas/neighborhoods/trade_areas_cbg": near(
    "/docs/concepts/metrics/trade-area-area",
  ),
  "/datasets/schemas/neighborhoods/demographics": near(
    "/docs/concepts/metrics/visitor-demographics-area",
  ),
}

/**
 * Prefix fallbacks, first match wins (most specific first). Catch pages we
 * never listed (Docusaurus tag listings, DaaS sub-pages, custom-location
 * schemas, deep links that were renamed over the years) so no old link dead
 * ends in a 404.
 */
const PREFIX: Array<[prefix: string, target: LegacyDocsTarget]> = [
  ["/datasets/delivery/daas", near("/docs/guides/bulk-export")],
  ["/datasets/delivery/api", near("/docs/api")],
  ["/datasets/delivery", near("/docs/get-started")],
  ["/datasets/schemas/custom-locations", near(CUSTOM_LOCATIONS_API)],
  ["/datasets/schemas", near("/docs/concepts/metrics")],
  ["/datasets/locations", near("/docs/concepts/location-types")],
  ["/datasets/metrics", near("/docs/concepts/metrics")],
  ["/datasets", near(GUIDES)],
  ["/category", near(GUIDES)],
  ["/tags", near(GUIDES)],
  ["/legal", eq(TERMS)],
]

/** Collapse repeated slashes, strip trailing slash, ensure leading slash. */
export function normalizeLegacyDocsPath(path: string): string {
  const collapsed = `/${path}`.replace(/\/{2,}/g, "/")
  return collapsed.length > 1 ? collapsed.replace(/\/+$/, "") : collapsed
}

/**
 * Resolve an old docs.unacast.com path (`/datasets/metrics/foot_traffic`) to
 * its Pine59 target, or null when the path is not a legacy docs path.
 */
export function resolveLegacyDocsPath(
  oldPath: string,
): LegacyDocsTarget | null {
  const path = normalizeLegacyDocsPath(oldPath)
  const exact = EXACT[path]
  if (exact) return exact
  for (const [prefix, target] of PREFIX) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return target
  }
  return null
}

/**
 * Append `?from-docs=<old path>` to a target, keeping any hash last.
 * Absolute targets (another Pine59 host) are returned as-is.
 */
export function legacyDocsRedirectUrl(
  target: LegacyDocsTarget,
  oldPath: string,
): string {
  if (/^https?:\/\//.test(target.to)) return target.to
  const [base, hash] = target.to.split("#", 2)
  const query = `${LEGACY_DOCS_PARAM}=${encodeURIComponent(normalizeLegacyDocsPath(oldPath))}`
  return `${base}?${query}${hash ? `#${hash}` : ""}`
}

/**
 * For a request pathname on this host (`/docs/datasets/metrics/foot_traffic`),
 * the redirect URL to the Pine59 page, or null if it is not a legacy path.
 * Only paths under `/docs` (optionally behind the retired secret prefix)
 * qualify; everything else is left to the normal 404 handling.
 */
export function legacyDocsRedirectForPathname(pathname: string): string | null {
  const match = /^(?:\/[^/]+)?\/docs(\/.*)?$/.exec(pathname)
  if (!match) return null
  const oldPath = match[1] ?? "/"
  const target = resolveLegacyDocsPath(oldPath)
  if (!target) return null
  return legacyDocsRedirectUrl(target, oldPath)
}
